import { useLocation, useSearchParams } from "react-router-dom";

/** Exported so callers and tests name the same parameter rather than a copy of it. */
export const STRIP_PARAM = "analytics:strip";

/**
 * The designed default: the strip is **visible**. A pipeline whose numbers had to be
 * summoned would be a pipeline most readers never saw the numbers of.
 */
export const STRIP_DEFAULT_VISIBLE = true;

/** Whether a search string asks for the strip hidden. Only `0` hides it; anything else is the default. */
export function stripVisibleFromSearch(search: string): boolean {
  return new URLSearchParams(search).get(STRIP_PARAM) !== "0";
}

/**
 * Whether the summary strip is shown — **the URL**, deviations only.
 *
 * It used to be `localStorage` (`pipeline:strip`), on the reasoning that this is a display
 * preference like `perPage` rather than part of the view. That reasoning lost to a plainer
 * requirement (2026-08-26): the developer wanted to *link* to a board with the numbers
 * showing, and a preference living in one browser cannot be linked. So it joins the filters,
 * the year and the view in the query string, where everything a reader would expect to travel
 * already lives.
 *
 * Its price, paid knowingly: hiding the strip no longer follows you between machines or
 * survives a fresh link. That is the lesser loss — a habit re-expressed with one click,
 * against a link that could not carry what it was sent to show.
 *
 * **Only the deviation is written.** `?analytics:strip=0` means hidden; the parameter is
 * deleted when the strip is shown, so a clean board is a clean URL and changing the designed
 * default moves every reader who never pressed the button.
 */
export function useStripVisible(): { visible: boolean; toggle: () => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const visible = searchParams.get(STRIP_PARAM) !== "0";

  return {
    visible,
    toggle: () => {
      // Read `window.location.search` rather than the snapshot, for the reason `SummaryStrip`
      // documents: a captured `searchParams` loses whatever another setter wrote in the same
      // handler.
      const next = new URLSearchParams(window.location.search);
      if (visible) next.set(STRIP_PARAM, "0");
      else next.delete(STRIP_PARAM);
      // `replace`, because showing and hiding the numbers is not a place to come back to — and
      // `state` explicitly, because a replace without it erases the background a detail modal
      // is drawn over.
      setSearchParams(next, { replace: true, state: location.state });
    },
  };
}
